import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  joinThread: (threadId: string) => void;
  leaveThread: (threadId: string) => void;
  sendTypingStart: (threadId: string, userName: string, userType: 'admin' | 'applicant') => void;
  sendTypingStop: (threadId: string, userName: string, userType: 'admin' | 'applicant') => void;
  onNewMessage: (callback: (data: any) => void) => void;
  onUserTyping: (callback: (data: any) => void) => void;
  onUserStoppedTyping: (callback: (data: any) => void) => void;
  offNewMessage: () => void;
  offUserTyping: () => void;
  offUserStoppedTyping: () => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Derive the Socket.IO URL:
    // - Prefer explicit VITE_SOCKET_URL if provided
    // - Otherwise, use VITE_API_URL with any trailing `/api` stripped off
    // - Finally, fall back to local backend
    const rawApiUrl = import.meta.env.VITE_API_URL as string | undefined;
    const socketUrl =
      (import.meta.env as any).VITE_SOCKET_URL ||
      (rawApiUrl ? rawApiUrl.replace(/\/api\/?$/, '') : undefined) ||
      'http://localhost:5000';

    const newSocket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
    });

    newSocket.on('connect', () => {
      console.log('Connected to server');
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from server');
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Connection error:', error);
      setIsConnected(false);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  const joinThread = (threadId: string) => {
    if (socket) {
      socket.emit('join-thread', threadId);
    }
  };

  const leaveThread = (threadId: string) => {
    if (socket) {
      socket.emit('leave-thread', threadId);
    }
  };

  const sendTypingStart = (threadId: string, userName: string, userType: 'admin' | 'applicant') => {
    if (socket) {
      socket.emit('typing-start', { threadId, userName, userType });
    }
  };

  const sendTypingStop = (threadId: string, userName: string, userType: 'admin' | 'applicant') => {
    if (socket) {
      socket.emit('typing-stop', { threadId, userName, userType });
    }
  };

  const onNewMessage = (callback: (data: any) => void) => {
    if (socket) {
      socket.on('new-message', callback);
    }
  };

  const onUserTyping = (callback: (data: any) => void) => {
    if (socket) {
      socket.on('user-typing', callback);
    }
  };

  const onUserStoppedTyping = (callback: (data: any) => void) => {
    if (socket) {
      socket.on('user-stopped-typing', callback);
    }
  };

  const offNewMessage = () => {
    if (socket) {
      socket.off('new-message');
    }
  };

  const offUserTyping = () => {
    if (socket) {
      socket.off('user-typing');
    }
  };

  const offUserStoppedTyping = () => {
    if (socket) {
      socket.off('user-stopped-typing');
    }
  };

  const value: SocketContextType = {
    socket,
    isConnected,
    joinThread,
    leaveThread,
    sendTypingStart,
    sendTypingStop,
    onNewMessage,
    onUserTyping,
    onUserStoppedTyping,
    offNewMessage,
    offUserTyping,
    offUserStoppedTyping,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
